import { useCallback, useLayoutEffect } from 'react';
import { StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { z } from 'zod';

import { FormProvider } from 'react-hook-form';
import { Toast, HeaderAction, Button, showErrorToUser, useForm, FormInput } from '@pletly/ui';
import { AuthContainer } from '../../../components/AuthContainer/AuthContainer';
import { Email } from '../../../components/Email';
import BackIcon from './back.svg';
import { withTranslation } from '../../../hoc/withTranslation';
import { useResetPasswordMutation } from './__generated__/resetPassword.generated';

const formSchema = z.object({
	email: z.string().email(),
});

function ForgotPassword() {
	const { t } = useTranslation();
	const { navigate } = useNavigation();
	const formMethods = useForm(formSchema);
	const { reset } = formMethods;

	const { setOptions, goBack } = useNavigation();
	const [resetPassword, { loading: isSending }] = useResetPasswordMutation();

	const handleSendPress: Parameters<typeof formMethods.handleSubmit>['0'] = useCallback(
		async ({ email }) => {
			if (isSending) {
				return;
			}

			try {
				await resetPassword({
					variables: {
						email: email.trim(),
					},
				});
				Toast.show({
					type: 'success',
					text1: t('forgot-password.sent-please-check-your-email'),
				});
				reset();
				navigate('Login');
			} catch (error) {
				showErrorToUser(error as Error);
			}
		},
		[isSending, navigate, resetPassword, t, reset],
	);

	const onSubmit = formMethods.handleSubmit(handleSendPress);

	useLayoutEffect(() => {
		setOptions({
			headerShown: true,
			header: () => (
				<SafeAreaView edges={['top']}>
					<HeaderAction onPress={goBack} IconComponent={<BackIcon style={backStyle} />} />
				</SafeAreaView>
			),
			headerTransparent: true,
		});
	});

	return (
		<AuthContainer
			title={t('login.forgot-password')}
			subtitle={t(
				'login.please-enter-your-email-an-email-will-be-sent-to-it-to-reset-your-password',
			)}
			noKeyboardAvoiding
		>
			<FormProvider {...formMethods}>
				<FormInput InputComponent={Email} name="email" autoFocus onSubmitEditing={onSubmit} />

				<Button
					color="primary"
					variant="filled"
					style={styles.send}
					onPress={onSubmit}
					loading={isSending}
				>
					{t('common.send')}
				</Button>
			</FormProvider>
		</AuthContainer>
	);
}

const backStyle = {
	margin: 8,
};
const styles = StyleSheet.create({
	send: {
		marginTop: 16,
	},
});

export default withTranslation(ForgotPassword);
